import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { useMemo, useCallback } from 'react'

import type { Invite, User } from '@/api/admin-types'
import { useDeleteInvite, useDeleteUser } from '@/api/api-mutations'
import { useGetMe, useGetUserSignInCredentials, useListInvites, useListUsers } from '@/api/react-query'
import { Container, ContainerHeader } from '@/components/base/container'
import { Copy } from '@/components/base/copy'
import { IconButton } from '@/components/base/icon-button'
import { usePrompt } from '@/components/base/prompt'
import { SectionRow } from '@/components/base/section-row'
import { StatusCell } from '@/components/base/status-cell'
import { Text } from '@/components/base/text'
import { toast } from '@/components/base/toast'
import { SingleColumnPage } from '@/components/page-layout'
import { useI18n } from '@/i18n/i18n-react'
import { Key } from '@/icons/key'
import { PencilSquare } from '@/icons/pencil-square'
import { Trash } from '@/icons/trash'
import { isAdminOrDeveloperRole } from '@/util/invite-role-label'
import { ConditionalRouteComponent } from '@/utils/route-utilities'

export const Route = createFileRoute('/_authenticated/settings/users/$userUuid')({
	component: () => (
		<ConditionalRouteComponent routePath={Route.id} pathsToRenderOn={['/edit', '/edit-sign-in-credentials']}>
			<UserOrInviteDetail />
		</ConditionalRouteComponent>
	),
	staticData: {
		crumb: ({ context: { L } }) => L.Settings.Users.Detail.Title()
	}
})

const inviteDeletableStatuses = new Set(['PENDING', 'REJECTED'])

type UserStatusDisplay = 'ACTIVE' | 'PENDING' | 'ACCEPTED' | 'EXPIRED' | 'REJECTED'

function inviteDetailDisplayStatus(invite: Invite): UserStatusDisplay {
	if (invite.status === 'PENDING' && invite.expiresAt <= Date.now()) {
		return 'EXPIRED'
	}
	return invite.status as UserStatusDisplay
}

function UserOrInviteDetail() {
	const { L } = useI18n()
	const { userUuid } = Route.useParams()
	const me = useGetMe()
	const users = useListUsers({ enabled: !!me.data })
	const invites = useListInvites({ enabled: !!me.data })

	const user = useMemo(() => users.data?.find(u => u.uuid === userUuid), [users.data, userUuid])
	const invite = useMemo(() => invites.data?.find(i => i.uuid === userUuid), [invites.data, userUuid])

	const isPending = me.isPending || users.isPending || invites.isPending

	if (isPending) {
		return (
			<SingleColumnPage>
				<Container className='p-6'>
					<ContainerHeader heading={L.Settings.Users.Detail.Title()} />
				</Container>
			</SingleColumnPage>
		)
	}

	if (user) {
		return <UserDetail user={user} />
	}

	if (invite) {
		return <InviteDetail invite={invite} />
	}

	return (
		<SingleColumnPage>
			<Container className='divide-y'>
				<ContainerHeader heading={L.Settings.Users.Detail.Title()} />
				<SectionRow title={L.Fields.Status()} value='-' />
			</Container>
		</SingleColumnPage>
	)
}

function UserDetail({ user }: { user: User }) {
	const { L } = useI18n()
	const navigate = useNavigate()
	const me = useGetMe()
	const isAdmin = isAdminOrDeveloperRole(me.data?.role)
	const isSelf = me.data?.uuid === user.uuid
	const prompt = usePrompt()
	const deleteUser = useDeleteUser()
	const credentials = useGetUserSignInCredentials({ userUuid: user.uuid }, { enabled: isAdmin })

	const handleDeleteUser = useCallback(async () => {
		const ok = await prompt({
			title: L.General.AreYouSure(),
			description: L.Settings.Users.DeleteUserDescription({ email: user.email }),
			confirmText: L.General.Delete(),
			cancelText: L.General.Cancel()
		})
		if (!ok) return
		const result = await deleteUser({ path: { userUuid: user.uuid } })
		if (result.isSuccess) {
			toast.success(L.Settings.Users.DeleteUserSuccess())
			void navigate({ to: '/settings/users' })
		} else {
			toast.error((result.response as any)?.message ?? L.Error.SomethingWentWrong())
		}
	}, [L, deleteUser, navigate, prompt, user.email, user.uuid])

	return (
		<SingleColumnPage hasOutlet>
			<Container className='divide-y'>
				<ContainerHeader
					heading={user.displayName || user.email}
					rightSide={
						isAdmin ? (
							<div className='flex items-center gap-x-1'>
								<Link from={Route.fullPath} to='./edit'>
									<IconButton tooltipText={L.General.Edit()}>
										<PencilSquare />
									</IconButton>
								</Link>
								{!isSelf ? (
									<IconButton
										tooltipText={L.General.Delete()}
										variant='transparent'
										onClick={() => void handleDeleteUser()}
									>
										<Trash className='text-ui-fg-subtle' />
									</IconButton>
								) : null}
							</div>
						) : null
					}
				/>
				<SectionRow title={L.Fields.Email()} value={user.email} />
				<SectionRow title={L.Fields.Role()} value={L.Enum.UserRole(user.role)} />
				<SectionRow
					title={L.Fields.CreatedAt()}
					value={new Date(user.createdAt).toLocaleDateString(undefined, {
						year: 'numeric',
						month: 'long',
						day: 'numeric'
					})}
				/>
			</Container>

			{isAdmin ? (
				<Container className='divide-y'>
					<ContainerHeader
						heading={L.Settings.Users.SignInCredentials.Title()}
						subHeading={
							<Text size='small' className='text-ui-fg-subtle'>
								{L.Settings.Users.SignInCredentials.Description()}
							</Text>
						}
						rightSide={
							<Link from={Route.fullPath} to='./edit-sign-in-credentials'>
								<IconButton tooltipText={L.General.Edit()}>
									<Key />
								</IconButton>
							</Link>
						}
					/>
					<SectionRow
						title={L.Settings.Users.SignInCredentials.SignInId()}
						value={credentials.data?.signInId ?? L.General.NotSet()}
					/>
					<SectionRow
						title={L.Settings.Users.SignInCredentials.Pin()}
						value={
							credentials.data?.hasPin
								? L.Settings.Users.SignInCredentials.PinSet()
								: L.Settings.Users.SignInCredentials.PinNotSet()
						}
					/>
				</Container>
			) : null}
		</SingleColumnPage>
	)
}

function InviteDetail({ invite }: { invite: Invite }) {
	const { L } = useI18n()
	const navigate = useNavigate()
	const me = useGetMe()
	const isAdmin = isAdminOrDeveloperRole(me.data?.role)
	const prompt = usePrompt()
	const deleteInvite = useDeleteInvite()

	const displayStatus = useMemo(() => inviteDetailDisplayStatus(invite), [invite])

	const statusLabel = useMemo(() => L.Enum.UserStatus(displayStatus), [displayStatus, L])

	const createdAtLabel = useMemo(
		() =>
			new Date(invite.createdAt).toLocaleDateString(undefined, {
				year: 'numeric',
				month: 'long',
				day: 'numeric'
			}),
		[invite.createdAt]
	)

	const expiresAtLabel = useMemo(
		() =>
			new Date(invite.expiresAt).toLocaleString(undefined, {
				year: 'numeric',
				month: 'long',
				day: 'numeric',
				hour: '2-digit',
				minute: '2-digit'
			}),
		[invite.expiresAt]
	)

	const statusColor = useMemo(() => {
		switch (displayStatus) {
			case 'ACCEPTED':
				return 'green' as const
			case 'PENDING':
				return 'blue' as const
			case 'EXPIRED':
				return 'grey' as const
			case 'REJECTED':
				return 'red' as const
			default:
				return 'grey' as const
		}
	}, [displayStatus])

	const canCopyInvite = isAdmin && invite.status === 'PENDING' && invite.expiresAt > Date.now() && invite.inviteLink

	const canDeleteInvite = isAdmin && inviteDeletableStatuses.has(invite.status)

	const handleDeleteInvite = useCallback(async () => {
		const ok = await prompt({
			title: L.General.AreYouSure(),
			description: L.Settings.Users.DeleteInviteDescription({ email: invite.email }),
			confirmText: L.General.Delete(),
			cancelText: L.General.Cancel()
		})
		if (!ok) return
		const result = await deleteInvite({ path: { inviteUuid: invite.uuid } })
		if (result.isSuccess) {
			toast.success(L.Settings.Users.DeleteInviteSuccess())
			void navigate({ to: '/settings/users' })
		} else {
			toast.error((result.response as any)?.message ?? L.Error.SomethingWentWrong())
		}
	}, [L, invite.email, invite.uuid, deleteInvite, navigate, prompt])

	return (
		<SingleColumnPage>
			<Container className='divide-y'>
				<ContainerHeader
					heading={invite.email}
					rightSide={
						canDeleteInvite ? (
							<IconButton
								tooltipText={L.General.Delete()}
								variant='transparent'
								onClick={() => void handleDeleteInvite()}
							>
								<Trash className='text-ui-fg-subtle' />
							</IconButton>
						) : null
					}
				/>
				<SectionRow title={L.Fields.Email()} value={invite.email} />
				<SectionRow title={L.Fields.Role()} value={L.Enum.UserRole(invite.role)} />
				<SectionRow
					title={L.Fields.Status()}
					value={<StatusCell color={statusColor}>{statusLabel}</StatusCell>}
				/>
				<SectionRow title={L.Fields.CreatedAt()} value={createdAtLabel} />
				<SectionRow title={L.Fields.ExpiresAt()} value={expiresAtLabel} />
				{canCopyInvite && invite.inviteLink ? (
					<SectionRow
						title={L.Settings.Users.CopyInviteLink()}
						value={
							<div className='flex items-center gap-2'>
								<span className='truncate text-sm text-ui-fg-subtle'>{invite.inviteLink}</span>
								<Copy
									content={invite.inviteLink}
									tooltipText={L.Settings.Users.CopyInviteLink()}
									variant='mini'
								/>
							</div>
						}
					/>
				) : null}
			</Container>
		</SingleColumnPage>
	)
}
